import React, { useContext, useState, useCallback } from "react";
import { LanguageContext } from "../../../../../Context";
import useDBO from "../../../hooks/useDBO";
import Lang from "../../../components/Lang";
import Loading from "../../../Loading";
import {
  Phone,
  Plus,
  Sparkles,
  MapPin,
  Flame,
  Table2,
  AlertTriangle,
  User,
} from "lucide-react";
import Staff from "../../../components/Staff";
import MarketingAddContactModal from "./MarketingAddContactModal";
import MarketingAllLeadsModal from "./MarketingAllLeadsModal";


const STAGE_CONFIG = {
  lead: { labelKey: "pipeline.lead", fallback: "Lead", color: "#64748b", bg: "#f1f5f9" },
  "add-zalo": { labelKey: "pipeline.addZalo", fallback: "Add Zalo", color: "#3b82f6", bg: "#eff6ff" },
  "book-test": { labelKey: "pipeline.bookTest", fallback: "Book Test", color: "#f59e0b", bg: "#fffbeb" },
  test: { labelKey: "pipeline.test", fallback: "Test", color: "#f97316", bg: "#fff7ed" },
  enrol: { labelKey: "pipeline.enrol", fallback: "Enrol", color: "#10b981", bg: "#ecfdf5" },
};

const HEAT_COLORS = {
  1: "#ef4444",
  2: "#f97316",
  3: "#eab308",
  4: "#22c55e",
  5: "#10b981",
};

export default function MarketingWidget() {
  const { l, language } = useContext(LanguageContext);
  const dashboard = useDBO("/marketing/dashboard", 60);
  const [addOpen, setAddOpen] = useState(false);
  const [allLeadsOpen, setAllLeadsOpen] = useState(false);

  const t = useCallback(
    (key, fallback) => {
      try {
        return l[key]?.[language] || fallback || key;
      } catch {
        return fallback || key;
      }
    },
    [l, language]
  );

  const dashboardRef = React.useRef(dashboard);
  dashboardRef.current = dashboard;

  React.useEffect(() => {
    if (dashboardRef.current.error) {
      const timer = setTimeout(() => dashboardRef.current.refresh(), 30000);
      return () => clearTimeout(timer);
    }
  }, [dashboard.error]);

  if (dashboard.loading && !dashboard.get) {
    if (dashboard.error) {
      return (
        <div className="home-widget flex flex-col h-full bg-white border border-slate-200 rounded-[24px] shadow-sm overflow-hidden">
          <div className="px-4 py-3 flex justify-between items-center bg-slate-50 shrink-0 border-b border-slate-100">
            <h3 className="font-heading text-[1.1rem] font-semibold text-slate-900 flex items-center gap-2">
              <Phone size={20} className="text-teal-800" />
              <Lang w="marketingWidget" />
            </h3>
            <button
              className="bg-white hover:bg-slate-50 text-slate-700 font-bold py-1 px-3 rounded-lg text-[0.6rem] border border-slate-200 shadow-sm transition-all active:scale-95 cursor-pointer uppercase tracking-tighter flex items-center gap-1"
              onClick={() => dashboard.refresh()}
            >
              <Lang w="retry" />
            </button>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center h-full text-center text-rose-500 py-10">
            <AlertTriangle size={36} className="mb-3" />
            <p className="text-sm font-medium"><Lang w="marketingLoadError" /></p>
          </div>
        </div>
      );
    }
    return <Loading />;
  }
  console.log(dashboard)
  const dashboardData = dashboard.get || { contacts: [], stats: { total: 0, inPipeline: 0, newContacts: 0, byStage: {} } };

  const contacts = dashboardData.contacts.slice(0, 8);
  const stats = dashboardData.stats;

  const daysSince = (iso) => {
    if (!iso) return null;
    const diff = Date.now() - new Date(iso).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  const formatPhone = (phone) => {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  const handleContactAdded = (newContact) => {
    if (dashboardData.contacts) {
      dashboard.set({
        ...dashboardData,
        contacts: [newContact, ...(dashboardData.contacts || [])],
        stats: {
          ...stats,
          total: (stats.total || 0) + 1,
          newContacts: (stats.newContacts || 0) + 1,
        },
      });
    }
  };

  return (
    <div className="home-widget flex flex-col h-full bg-white border border-slate-200 rounded-[24px] shadow-sm overflow-hidden">
      <div
        className={`px-4 py-3 flex justify-between items-center bg-slate-50 shrink-0 ${
          contacts.length > 0 ? "border-b border-slate-100" : ""
        }`}
      >
        <div className="flex flex-col">
          <h3 className="font-heading text-[1.1rem] font-semibold text-slate-900 flex items-center gap-2">
            <Phone size={20} className="text-teal-800" />
            <Lang w="marketingWidget" />
          </h3>
        </div>
        <div className="flex items-center gap-3">
          <button
            className="bg-white hover:bg-slate-50 text-slate-700 font-bold py-1 px-3 rounded-lg text-[0.6rem] border border-slate-200 shadow-sm transition-all active:scale-95 cursor-pointer uppercase tracking-tighter flex items-center gap-1"
            onClick={() => setAllLeadsOpen(true)}
          >
            <Table2 size={12} />
            <Lang w="viewAllLeads" />
          </button>
          <button
            className="bg-teal-600 text-white font-bold py-1 px-3 rounded-lg text-[0.6rem] shadow-sm transition-all active:scale-95 cursor-pointer uppercase tracking-tighter flex items-center gap-1"
            
            onClick={() => setAddOpen(true)}
          >
            <Plus size={12} />
            <Lang w="addContact" />
          </button>
          {stats.total > 0 && (
            <div className="flex items-center gap-2 bg-teal-600 text-white px-3 py-1 rounded-lg shadow-sm">
              <span className="text-sm font-black leading-none">
                {stats.total}
              </span>
              <span className="text-[0.6rem] font-bold opacity-80 uppercase tracking-tighter">
                <Lang w="contacts" />
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 bg-white max-h-[500px] scrollbar-slim">
        {contacts.length > 0 ? (
          <div className="flex flex-col gap-2">
            {contacts.map((contact) => {
              const stage = contact.pipelineStage;
              const stageConfig = stage
                ? STAGE_CONFIG[stage]
                : null;
              const lastContactDays = daysSince(contact.lastContacted);
              const heatValue = contact.heat || 0;
              const heatColor = HEAT_COLORS[heatValue] || "#94a3b8";

              return (
                <div
                  key={contact._id}
                  className="flex items-center justify-between gap-4 p-2.5 bg-slate-50/50 rounded-xl border border-slate-100 hover:border-teal-100 hover:bg-teal-50/30 transition-all group"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="shrink-0 text-[0.65rem] font-[800] text-slate-500 bg-white px-2 py-1.5 rounded-lg border border-slate-200 shadow-sm whitespace-nowrap min-w-[90px] text-center tabular-nums">
                      {formatPhone(contact.phone)}
                    </div>

                    <div className="flex flex-col min-w-0">
                      <div className="font-[800] text-slate-800 text-[0.7rem] leading-tight truncate">
                        {contact.name || (
                          <span className="italic text-slate-400">
                            {t("pipeline.noName", "No name")}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {contact.location && (
                          <span className="text-[0.6rem] font-bold text-slate-400 flex items-center gap-1">
                            <MapPin size={9} className="opacity-70" />
                            <span className="max-w-[80px] truncate">
                              {contact.location}
                            </span>
                          </span>
                        )}
                        {heatValue > 0 && (
                          <span className="flex items-center gap-0.5">
                            <Flame
                              size={9}
                              style={{ color: heatColor }}
                            />
                            <span
                              className="text-[0.6rem] font-bold"
                              style={{ color: heatColor }}
                            >
                              {heatValue}
                            </span>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <User size={9} className="opacity-70" style={{ color: contact.salesConsultant ? undefined : "#f59e0b" }} />
                        {contact.salesConsultant ? (
                          <span className="text-[0.6rem] font-bold text-slate-400 truncate max-w-[80px]">
                            <Staff id={contact.salesConsultant} />
                          </span>
                        ) : (
                          <span className="text-[0.6rem] font-semibold text-amber-500 italic">
                            <Lang w="waiting" />...
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    {stageConfig ? (
                      <span
                        className="px-2 py-0.5 rounded-full text-[0.55rem] font-bold uppercase tracking-wider"
                        style={{
                          color: stageConfig.color,
                          backgroundColor: stageConfig.bg,
                          border: `1px solid ${stageConfig.color}30`,
                        }}
                      >
                        {t(stageConfig.labelKey, stageConfig.fallback)}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[0.55rem] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 border border-slate-200">
                        {t("marketingNew", "New")}
                      </span>
                    )}

                    {lastContactDays !== null && (
                      <div
                        className="text-[0.6rem] font-bold flex items-center gap-0.5"
                        style={{
                          color:
                            lastContactDays <= 1
                              ? "#10b981"
                              : lastContactDays <= 3
                              ? "#f59e0b"
                              : "#ef4444",
                        }}
                      >
                        {lastContactDays === 0
                          ? t("pipeline.today", "Today").toLowerCase()
                          : `${lastContactDays}d`}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center text-slate-400 py-10">
            <Sparkles size={36} className="mb-3 opacity-30" />
            <p className="text-sm font-medium">
              <Lang w="noMarketingContacts" />
            </p>
            <button
              className="mt-3 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 px-4 py-2 rounded-[10px] transition-all cursor-pointer flex items-center gap-1"
              
              onClick={() => setAddOpen(true)}
            >
              <Plus size={13} />
              <Lang w="addFirstContact" />
            </button>
          </div>
        )}
      </div>

      <MarketingAddContactModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={handleContactAdded}
      />

      <MarketingAllLeadsModal
        open={allLeadsOpen}
        onClose={() => setAllLeadsOpen(false)}
        contacts={dashboardData.contacts}
        stats={stats}
        t={t}
        formatPhone={formatPhone}
      />
    </div>
  );
}